import { useCallback, useMemo } from "react";

import * as audienceApi from "@/api";
import { useAudienceDispatch } from "@/context";
import { useClient, useServices } from "@/hooks";
import { Audience } from "@/types";
import { audienceListPath, unwrap } from "@/utils";
import { useAudienceTask } from "./useAudienceTask";

type AudienceTask = ReturnType<typeof useAudienceTask>;

/**
 * Changes to a saved audience: rename, delete, share, unshare and clone.
 * Pass a `task` to share its action state, e.g. with the rest of a page,
 * otherwise the mutations get their own.
 */
export const useAudienceMutations = (audience?: Audience, task?: AudienceTask) => {
  const { nav, ui } = useServices();
  const dispatch = useAudienceDispatch();
  const client = useClient();
  const ownTask = useAudienceTask();
  const { action, run } = task ?? ownTask;

  const rename = useCallback(async (audienceName: string, projectName: string | null) => {
    if (!audience) return;
    const audienceId = audience.audience_id;

    await run('update', 'Updating Audience...', async ({ token }) => {
      const response = await audienceApi.updateAudienceName(token, audienceId, audienceName, projectName);
      ui.closeDialog();
      unwrap(response);

      dispatch({
        type: "CHANGE_NAME",
        payload: { audienceId, audienceName, projectName },
      });
    }, { success: "Audience name updated successfully", notifyError: true });
  }, [audience, dispatch, run, ui]);

  const remove = useCallback(async (redirect?: string) => {
    if (!audience) return;

    await run('delete', 'Deleting Audience...', async ({ token }) => {
      const data = unwrap(await audienceApi.deleteAudience(
        token,
        audience.audience_name,
        audience.audience_id,
        audience.audience_type,
        client,
      ));

      dispatch({ type: "DELETE_AUDIENCE", payload: data });
      ui.closeDialog();
      redirect && nav.navigate(redirect);
    }, { success: "Audience deleted successfully", notifyError: true });
  }, [audience, client, dispatch, nav, run, ui]);

  const share = useCallback(async (ids: number[]) => {
    if (!audience) return;

    await run('share', 'Sharing Audience...', async ({ token }) => {
      const response = await audienceApi.shareAudience(token, audience.audience_id, !!audience.shared, ids);
      ui.closeDialog();
      unwrap(response);

      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
    }, { success: "Audience shared successfully", notifyError: true });
  }, [audience, dispatch, run, ui]);

  // Removes an audience shared with the user, then returns to its list
  const unshare = useCallback(async () => {
    if (!audience) return;

    await run('delete', 'Removing Shared Audience...', async ({ token }) => {
      unwrap(await audienceApi.deleteShare(token, audience.audience_id));

      ui.closeDialog();
      nav.navigate(audienceListPath(audience));
    }, { success: "Shared Audience removed successfully", notifyError: true });
  }, [audience, nav, run, ui]);

  const clone = useCallback(async () => {
    if (!audience) return;

    await run('clone', 'Cloning Audience...', async ({ token }) => {
      const response = await audienceApi.cloneAudience(token, audience.audience_id, client);
      ui.closeDialog();

      dispatch({ type: "GET_AUDIENCE_LIST", payload: unwrap(response) });
    }, { success: "Audience cloned successfully", notifyError: true });
  }, [audience, client, dispatch, run, ui]);

  return useMemo(
    () => ({ rename, remove, share, unshare, clone, action }),
    [rename, remove, share, unshare, clone, action]
  );
}

export type AudienceMutations = ReturnType<typeof useAudienceMutations>;
